import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, MapPin, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { COMPANY } from "../../lib/site-content";
import { Reveal } from "./Reveal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const CATEGORIES = [
  "Bier / Wijn / Sterkedrank",
  "Energieproducten & Elektriciteit",
  "Alcoholvrije dranken & Koffie",
  "Transport & Logistiek",
  "Anders",
];

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    category: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
    if (status === "error") setStatus("idle");
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Vul uw naam in.";
    if (!form.company.trim()) e.company = "Vul uw bedrijfsnaam in.";
    if (!form.email.trim()) e.email = "Vul uw e-mailadres in.";
    else if (!emailRe.test(form.email)) e.email = "Ongeldig e-mailadres.";
    if (!form.category) e.category = "Kies een categorie.";
    if (!form.message.trim()) e.message = "Vul uw bericht in.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("request failed");
      setStatus("success");
    } catch (err) {
      setStatus("error");
    }
  };

  const fieldCls = (k) =>
    `w-full rounded-xl border bg-white px-4 py-3 text-[#0F172A] outline-none transition-colors duration-200 placeholder:text-slate-400 focus:border-[#F59E0B] focus:ring-2 focus:ring-[#F59E0B]/20 ${
      errors[k] ? "border-red-400" : "border-slate-200"
    }`;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-white py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left */}
          <Reveal className="lg:col-span-5">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
              Contact
            </p>
            <h2 className="font-display text-3xl font-bold tracking-tight text-[#0F172A] sm:text-4xl lg:text-5xl">
              Klaar voor een Zorgeloze Fiscale Flow?
            </h2>
            <p className="mt-5 max-w-md text-lg text-slate-600">
              Vraag uw gratis Accijns Quickscan aan. We nemen binnen één werkdag
              contact met u op.
            </p>

            <div className="mt-10 space-y-4">
              {[
                { icon: Mail, label: "E-mail", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
                { icon: Phone, label: "Telefoon", value: COMPANY.phone, href: `tel:${COMPANY.phone}` },
                { icon: MapPin, label: "Adres", value: COMPANY.address },
              ].map((c) => (
                <a
                  key={c.label}
                  href={c.href || undefined}
                  className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-[#F8FAFC] p-4 transition-colors duration-200 hover:border-slate-300"
                >
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#0F172A] text-white">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-400">
                      {c.label}
                    </span>
                    <span className="block font-semibold text-[#0F172A]">
                      {c.value}
                    </span>
                  </span>
                </a>
              ))}
            </div>
          </Reveal>

          {/* Right - Form */}
          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-[0_20px_50px_rgba(15,23,42,0.08)] sm:p-9">
              <AnimatePresence mode="wait">
                {status === "success" ? (
                  <motion.div
                    key="success"
                    data-testid="contact-success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex min-h-[420px] flex-col items-center justify-center text-center"
                  >
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                      className="grid h-16 w-16 place-items-center rounded-full bg-[#F59E0B]/15 text-[#F59E0B]"
                    >
                      <CheckCircle2 className="h-9 w-9" />
                    </motion.span>
                    <h3 className="mt-6 font-display text-2xl font-bold text-[#0F172A]">
                      Bedankt, {form.name.split(" ")[0]}!
                    </h3>
                    <p className="mt-3 max-w-sm text-slate-600">
                      Uw aanvraag is verzonden. Ons team neemt binnen één
                      werkdag contact met u op voor uw gratis Quickscan.
                    </p>
                    <button
                      data-testid="contact-reset"
                      onClick={() => {
                        setForm({ name: "", company: "", email: "", phone: "", category: "", message: "" });
                        setStatus("idle");
                      }}
                      className="mt-8 rounded-full border border-slate-300 px-6 py-3 text-sm font-bold text-[#0F172A] transition-colors hover:border-[#0F172A]"
                    >
                      Nieuwe aanvraag
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    data-testid="contact-form"
                    onSubmit={submit}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="grid grid-cols-1 gap-5 sm:grid-cols-2"
                    noValidate
                  >
                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-[#0F172A]">
                        Naam
                      </label>
                      <input
                        data-testid="input-name"
                        className={fieldCls("name")}
                        placeholder="Jan Janssens"
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                      />
                      {errors.name && (
                        <p className="mt-1 text-xs font-medium text-red-500">{errors.name}</p>
                      )}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-[#0F172A]">
                        Bedrijfsnaam
                      </label>
                      <input
                        data-testid="input-company"
                        className={fieldCls("company")}
                        placeholder="Brouwerij BV"
                        value={form.company}
                        onChange={(e) => set("company", e.target.value)}
                      />
                      {errors.company && (
                        <p className="mt-1 text-xs font-medium text-red-500">{errors.company}</p>
                      )}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-[#0F172A]">
                        E-mail
                      </label>
                      <input
                        data-testid="input-email"
                        className={fieldCls("email")}
                        placeholder="jan@brouwerij.be"
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                      />
                      {errors.email && (
                        <p className="mt-1 text-xs font-medium text-red-500">{errors.email}</p>
                      )}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-[#0F172A]">
                        Telefoon
                      </label>
                      <input
                        data-testid="input-phone"
                        className={fieldCls("phone")}
                        placeholder="+32 ..."
                        value={form.phone}
                        onChange={(e) => set("phone", e.target.value)}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1.5 block text-sm font-semibold text-[#0F172A]">
                        Productcategorie
                      </label>
                      <Select
                        value={form.category}
                        onValueChange={(v) => set("category", v)}
                      >
                        <SelectTrigger
                          data-testid="select-category"
                          className={`h-[50px] rounded-xl border bg-white px-4 text-[#0F172A] ${
                            errors.category ? "border-red-400" : "border-slate-200"
                          }`}
                        >
                          <SelectValue placeholder="Selecteer een categorie" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {CATEGORIES.map((c) => (
                            <SelectItem key={c} value={c} data-testid={`category-${c}`}>
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.category && (
                        <p className="mt-1 text-xs font-medium text-red-500">{errors.category}</p>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1.5 block text-sm font-semibold text-[#0F172A]">
                        Bericht
                      </label>
                      <textarea
                        data-testid="input-message"
                        rows={4}
                        className={`${fieldCls("message")} resize-none`}
                        placeholder="Beschrijf kort uw situatie of vraag..."
                        value={form.message}
                        onChange={(e) => set("message", e.target.value)}
                      />
                      {errors.message && (
                        <p className="mt-1 text-xs font-medium text-red-500">{errors.message}</p>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <button
                        type="submit"
                        data-testid="contact-submit"
                        disabled={status === "loading"}
                        className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#F59E0B] px-7 py-4 text-sm font-bold text-[#0F172A] shadow-[0_12px_28px_rgba(245,158,11,0.35)] transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-70 sm:w-auto"
                      >
                        {status === "loading" ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" /> Verzenden...
                          </>
                        ) : (
                          <>
                            Aanvraag Verzenden
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                          </>
                        )}
                      </button>
                      {status === "error" && (
                        <p
                          data-testid="contact-error"
                          className="mt-3 text-sm font-medium text-red-500"
                        >
                          Er ging iets mis bij het verzenden. Probeer het
                          opnieuw of mail ons rechtstreeks.
                        </p>
                      )}
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
